/* eslint-disable react-hooks/exhaustive-deps */
/** @jsxImportSource @emotion/react */
import tw from 'twin.macro';

import { numberNoArrow } from 'components/shared/twin.styles';
import { ceil, toNumber } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCounter } from 'react-use';
import { RootState } from 'reducers';
import { changePage } from 'slices/pagination.slice';
import { Keys } from 'utils/enum';
import { setSelectedRows } from 'actions';
import { setSelectedClips } from 'slices/clips.slice';

import {
  clampLibraryPage,
  isSemanticBrowse,
  pageSizeOptionsFor,
  semanticMaxPage,
  validPageSize,
} from 'utils/libraryPagination.util';
import { useModeSwitchSearch } from './useModeSwitchSearch';

const DEFAULT_MIN_PAGE_NUMBER = 1;

interface IProps {}

export const LibraryPagination = (props: IProps) => {
  const paginationReducer = useSelector((state: RootState) => state.pagination);
  const library = useSelector((state: RootState) => state.library);
  const collection = useSelector((state: RootState) => state.collection);
  const isSemanticSearch = useSelector(
    (state: RootState) => state.advancedSearch.isSemanticSearch,
  );
  const searchBy = useSelector(
    (state: RootState) => state.advancedSearch.searchParams.searchBy,
  );
  // Same test the fetch uses, on the same committed search text.
  const isBrowse = isSemanticBrowse(library.searchTerm, searchBy);

  const pageSize = paginationReducer.page_size;
  const resultCount =
    library.data?.count ?? library.data?.total?.totalFiles ?? 0;
  // Items reachable by paging. The result count shown elsewhere stays the
  // engine's full total.
  const pagedCount = (count: number, size: number) =>
    isSemanticSearch && !isBrowse
      ? Math.min(count, semanticMaxPage(size) * size)
      : count;

  const [totalPage, setTotalPage] = useState(
    ceil(pagedCount(resultCount, pageSize) / pageSize) || 1,
  );

  const [pageNumber, { inc: nextPage, dec: prevPage, set: setPageNumber }] =
    useCounter(
      paginationReducer.page_number,
      totalPage,
      DEFAULT_MIN_PAGE_NUMBER,
    );
  const [pageInput, setPageInput] = useState<string>(
    paginationReducer.page_number.toString(),
  );

  const dispatch = useDispatch();
  const { isModeSwitched, searchInNewMode } = useModeSwitchSearch();

  useEffect(() => {
    setTotalPage(ceil(pagedCount(resultCount, pageSize) / pageSize) || 1);
  }, [resultCount, pageSize, isSemanticSearch, isBrowse]);

  useEffect(() => {
    setPageInput(paginationReducer.page_number.toString());
  }, [paginationReducer.page_number]);

  const dispatchPrevPage = () => {
    if (isModeSwitched) return searchInNewMode();
    prevPage();

    dispatch(
      changePage({
        page_number: paginationReducer.page_number - 1,
        page_size: pageSize,
      }),
    );
    resetSelection();
  };

  const dispatchNextPage = () => {
    if (isModeSwitched) return searchInNewMode();
    nextPage();

    dispatch(
      changePage({
        page_number: paginationReducer.page_number + 1,
        page_size: pageSize,
      }),
    );
    resetSelection();
  };

  const dispatchGotoPage = () => {
    if (isModeSwitched) return searchInNewMode();
    const targetPage = getPossiblePage(pageInput);

    if (paginationReducer.page_number === targetPage) {
      setPageInput(String(targetPage));
      return;
    }

    dispatch(
      changePage({
        page_number: targetPage,
        page_size: pageSize,
      }),
    );
    setPageNumber(targetPage);
    setPageInput(String(targetPage));
    resetSelection();
  };

  const resetSelection = () => {
    dispatch(setSelectedRows([]));
    dispatch(setSelectedClips([]))
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === Keys.ENTER || e.key === Keys.ENTER_NUMPAD) {
      dispatchGotoPage();
    }
  };

  const getPossiblePage = (num: number | string) => {
    return clampLibraryPage(
      toNumber(num),
      pageSize,
      pagedCount(resultCount, pageSize),
    );
  };

  const handleChangePageSize = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPageSize = +e.target.value;
    if (isModeSwitched) return searchInNewMode(newPageSize);

    const { count, total } = library.data;
    const itemCount = pagedCount(count ?? total?.totalFiles ?? 0, newPageSize);

    const newTotalPage = ceil(itemCount / newPageSize);
    setTotalPage(newTotalPage);

    // The old page number points at different items under the new size.
    setPageNumber(1);
    setPageInput('1');
    dispatch(changePage({ page_number: 1, page_size: newPageSize }));

    resetSelection();
  };

  return library?.data?.count != null ? (
    <div tw="grid grid-flow-col space-x-8 justify-between items-center">
      <>
        <div>
          <button
            tw="border-0 h-16 w-44 rounded-md my-12 background[#5551ff] text-xl hover:background[#5551ff]"
            className="button"
            onClick={dispatchPrevPage}
            disabled={paginationReducer.page_number <= 1}
          >
            Previous
          </button>
        </div>
        <div tw="flex items-center">
          <input
            tw="width[5.5rem]! text-center outline-none mr-2 mb-0!"
            css={numberNoArrow}
            type="number"
            value={pageInput || 1}
            onKeyDown={handleKeyDown}
            // onBlur={dispatchGotoPage}
            onChange={(e) => setPageInput(e.target.value)}
            min={1}
            max={totalPage}
          />
          <div tw="whitespace-nowrap">of {totalPage}</div>
        </div>
        <div>
          <button
            tw="border-0 h-16 w-44 rounded-md my-12 background[#5551ff] text-xl hover:background[#5551ff]"
            className="button"
            onClick={dispatchNextPage}
            disabled={paginationReducer.page_number >= totalPage}
          >
            Next
          </button>
        </div>
      </>
      <div tw="">
        <span tw="mr-2.5">Show</span>
        <select
          tw="h-16 w-32 my-12"
          css={[collection.showSidebar && tw`(my-0)!`]}
          onChange={handleChangePageSize}
          // The toggle does not refetch, so page_size can still hold 60/120
          // from the regular search. Show the size the next fetch will use.
          value={validPageSize(pageSize, isSemanticSearch, isBrowse)}
        >
          {pageSizeOptionsFor(isSemanticSearch, isBrowse).map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              {pageSize}
            </option>
          ))}
        </select>
      </div>
    </div>
  ) : (
    <></>
  );
};
